/**
 * Consider adding a short description of the feature flag and how it
 * will be used.
 *
 * If the feature flag is no longer in use, remove it from this enum.
 */
export var FeatureFlags;
(function (FeatureFlags) {
    /** Organization-scoped, off by default. Retry missing cross-project refs with sibling models injected through a generated dbt plugin. */
    FeatureFlags["CrossSourceDbtRefs"] = "cross-source-dbt-refs";
    FeatureFlags["DbtExplicitCredentials"] = "dbt-explicit-credentials";
    /**
     * Agent identity gates agent query identity, organisation settings and
     * Snowflake agent sign-in. Off by default; organization scope; no handler.
     */
    FeatureFlags["AgentIdentity"] = "agent-identity";
    FeatureFlags["OAuthScopeEnforcement"] = "oauth-scope-enforcement";
    FeatureFlags["MultiConnectionProjects"] = "multi-connection-projects";
    /** Extra warehouse connections of a different type to the project's original connection. */
    FeatureFlags["MixedWarehouseConnections"] = "mixed-warehouse-connections";
    /** Backend-provisioned usage analytics projects. */
    FeatureFlags["AnalyticsProject"] = "analytics-project";
    /* Show user groups */
    FeatureFlags["UserGroupsEnabled"] = "user-groups-enabled";
    /* Gate new timezone features: warehouse session timezone, timezone-aware
       DATE_TRUNC, per-viewer (user-profile) timezone resolution, result
       formatting, etc. Temporary — remove once stable. */
    FeatureFlags["EnableTimezoneSupport"] = "enable-timezone-support";
    /**
     * Enable the dynamic calculation of series color, when not manually set on the chart config.
     * This aims to make the colors more consistent, depending on the groups, but this could cause the opposite effect.
     * For more details, see https://github.com/lightdash/lightdash/issues/13831
     */
    FeatureFlags["CalculateSeriesColor"] = "calculate-series-color";
    /**
     * Enable the ability to show the warehouse execution time and total time in the chart tile.
     */
    FeatureFlags["ShowExecutionTime"] = "show-execution-time";
    /**
     * Enable the ability to create custom visualizations with AI
     */
    FeatureFlags["AiCustomViz"] = "ai-custom-viz";
    /**
     * Enable admin user impersonation. When disabled, impersonation
     * actions are blocked and active sessions are cleared.
     */
    FeatureFlags["UserImpersonation"] = "user-impersonation";
    /**
     * Keep visited dashboard tabs mounted in the DOM (hidden) for instant
     * re-switching. Enabled by default; disabled per-org for orgs where
     * large dashboards spiked browser memory to 3 GB+ from accumulated
     * tab content.
     */
    FeatureFlags["DashboardTabsInMemory"] = "dashboard-tabs-in-memory";
    /**
     * Enable dashboard controls: the new bar and sidebar for filter
     * controls.
     * Rolled out per organization. Temporary: remove once the new bar
     * replaces the shipped one.
     */
    FeatureFlags["DashboardControls"] = "dashboard-controls";
    /**
     * Enable data apps feature. Disabled by default. Self-hosted
     * deployments can force it on with APPS_RUNTIME_ENABLED=true, which
     * the config parser translates into the enable allowlist. Does not gate
     * Chart Studio, which is available with a valid licence and a configured
     * sandbox provider, but still keeps installed chart types usable.
     */
    FeatureFlags["EnableDataApps"] = "enable-data-apps";
    /**
     * AI access (in-app agent, Slack agent, MCP) skips the results cache,
     * earlier results and pre-aggregates. Off by default; organization scope.
     */
    FeatureFlags["AiAccessSkipResultsCache"] = "ai-access-skip-results-cache";
    /**
     * Per-organization gate for declaring custom npm dependencies in data
     * apps. Disabled by default; self-hosted instances can enable it globally
     * via LIGHTDASH_ENABLE_FEATURE_FLAGS.
     */
    FeatureFlags["EnableDataAppCustomDependencies"] = "enable-data-app-custom-dependencies";
    /**
     * Per-organization rollout of AI analysis in data apps (detect anomalies,
     * investigate with an agent). Gates the endpoints and the admin toggle;
     * the org's `dataAppRuntimeAiEnabled` setting is the separate customer
     * consent. Disabled by default.
     */
    FeatureFlags["EnableDataAppAnalysis"] = "enable-data-app-analysis";
    /**
     * Per-organization rollout of AI analysis on classic dashboards. Gates the
     * dashboard analysis endpoints and entry points; shares the org's
     * `dataAppRuntimeAiEnabled` consent setting and daily analysis budgets
     * with data-app analysis. Disabled by default.
     */
    FeatureFlags["EnableDashboardAnalysis"] = "enable-dashboard-analysis";
    /**
     * Per-organization gate for automatic (obscured) data app thumbnail
     * capture and its settings card. Disabled by default.
     */
    FeatureFlags["EnableDataAppAutomaticThumbnails"] = "enable-data-app-automatic-thumbnails";
    /**
     * Per-user / per-organization gate for the data app state store
     * (`useAppState` / `useSharedAppState` in the SDK and its endpoints).
     * Resolves user override, then organization, then instance default.
     * Disabled by default on Cloud and self-hosted.
     */
    FeatureFlags["EnableDataAppState"] = "enable-data-app-state";
    /**
     * Enable Autopilot project health agent.
     */
    FeatureFlags["AiAutopilot"] = "ai-autopilot";
    /**
     * Enable the Hexbin (H3 hexagonal binning) layer type for Map charts.
     * Gates the option in the Map Type segmented control. Existing charts
     * already saved with the hexbin layer continue to render either way.
     */
    FeatureFlags["HexbinMap"] = "hexbin-map";
    /**
     * Show the per-organization Single Sign-On settings panel (Azure AD and
     * future SSO providers). Off by default while the domain-claim trust
     * model is hardened — see security review notes. Enable per-org for
     * vetted customers on shared multi-org instances.
     */
    FeatureFlags["SsoOrganizationSettings"] = "sso-organization-settings";
    /**
     * Enable query results caching. DB value (user/org override or flag
     * default) takes precedence; falls back to the RESULTS_CACHE_ENABLED env
     * var when no DB row is set. Lets shared-instance customers (eu1/app)
     * opt in per-org without a redeploy.
     */
    FeatureFlags["ResultsCacheEnabled"] = "results-cache-enabled";
    /**
     * Show a persistent trial warning banner for an organization on shared
     * instances. This does not block product access.
     */
    FeatureFlags["OrganizationTrialWarning"] = "organization-trial-warning";
    /**
     * Block an organization from running queries because its trial has
     * expired. Stronger than OrganizationTrialWarning — this DOES block a
     * product action (query execution). Off by default; enable per-org.
     */
    FeatureFlags["OrganizationTrialBlock"] = "organization-trial-block";
    /**
     * Show the coding-agent project onboarding flow. This gates only the UI;
     * the CLI, APIs, and installed skills remain available independently.
     */
    FeatureFlags["CodingAgentOnboarding"] = "coding-agent-onboarding";
    /**
     * Allow storing long-lived GitHub personal access tokens as GitHub MCP
     * credentials (guided connect flow and generic bearer endpoints targeting
     * the hosted GitHub MCP URL). Off by default — orgs should authenticate
     * via the Lightdash GitHub App, which mints a short-lived installation
     * token per run and stores no long-lived secret. When off, previously
     * stored PATs are also ignored at runtime. Enable
     * per-org only as a stopgap for orgs that cannot install the GitHub App.
     */
    FeatureFlags["AiMcpGithubPat"] = "ai-mcp-github-pat";
    /**
     * Gate the whole new onboarding experience as one unit: email-only
     * signup (register collects only an email; ownership proven via email
     * OTP), the full-page organization setup experience shown after
     * registration, and the dbt-less "connect to your warehouse" onboarding
     * path including the Snowflake "connect via CLI (SSO)" auth method in
     * project creation. Off by default. Note: the register page evaluates
     * this flag anonymously, so per-org overrides do not apply there —
     * enable instance-wide (or for everyone in posthog) to turn on signup.
     */
    FeatureFlags["NewOnboarding"] = "new-onboarding";
    /**
     * Cloud-only: let an organization send report/notification emails from
     * their own verified domain (email whitelabelling) instead of the
     * Lightdash address. Gates both the setup UI and the admin API. Requires a
     * Postmark account token to be configured on the instance — self-hosters
     * without one can't self-serve, so the feature stays hidden. Off by
     * default; enable per-org.
     */
    FeatureFlags["EmailWhitelabel"] = "email-whitelabel";
    /**
     * Advertise compact filter expressions to AI agent and MCP metric-query
     * tools, resolving them to the existing filter model at the tool boundary.
     * Off by default while the public contract is rolled out per organization.
     */
    FeatureFlags["AiFilterExpressions"] = "ai-filter-expressions";
    /**
     * Enable the async compose SQL endpoint
     * (POST /api/v2/projects/{projectUuid}/query/compose-sql), which runs
     * DuckDB SQL over other queries' results, exposed as named tables via
     * the request's references map ({"orders": "<queryUuid>"}).
     * Off by default; on in preview/dev environments via
     * PREVIEW_ENABLED_FEATURE_FLAGS.
     */
    FeatureFlags["ComposeSqlRunner"] = "compose-sql-runner";
    /**
     * Enable api query sources: queries sent live to an external API (HogQL
     * to PostHog) through a project connection, and the Live data sources
     * project settings page. Off by default; enable per org.
     */
    FeatureFlags["ApiQuerySources"] = "api-query-sources";
    /**
     * Enable the "My query history" page
     * (/projects/{projectUuid}/query-history) and its listing endpoint
     * (GET /api/v2/projects/{projectUuid}/query/history). Off by default.
     */
    FeatureFlags["QueryHistory"] = "query-history";
    /**
     * Configurable retention for AI agent threads. Off by default; enabled
     * per-org on demand for enterprise customers.
     */
    FeatureFlags["AiThreadRetention"] = "ai-thread-retention";
    /**
     * Per-organization gate for experimental surfaces in Lightdash Desktop.
     * Desktop reads it once per sign-in. Off by default; enable per org.
     */
    FeatureFlags["DesktopExperiments"] = "desktop-experiments";
    /**
     * External data sources: upload CSV files or connect Google Sheets as
     * project tables. Tables are ingested to typed parquet in object storage,
     * generated as explores (ExploreType.EXTERNAL_SOURCE), and queried on the
     * DuckDB compose engine — so this requires the enterprise pre-aggregates
     * engine and its S3 configuration. Off by default.
     */
    FeatureFlags["ExternalSources"] = "external-sources";
    /**
     * Prevent users from deleting their own AI agent threads. Admins can
     * still delete threads from the agent admin threads view. Off by default.
     */
    FeatureFlags["AiDisableThreadDeletion"] = "ai-disable-thread-deletion";
    /**
     * Enables the chart type library — browsing and installing official
     * chart types from the configured chart registry — and, as an either/or
     * gate with Chart Studio availability and `EnableDataApps`, usage of
     * installed chart types (picking, configuring, rendering). Authoring,
     * editing, and forking chart types follow Chart Studio availability
     * (a valid licence and a configured sandbox provider), not this flag.
     * Default: off.
     */
    FeatureFlags["ChartTypeRegistry"] = "chart-type-registry";
    /**
     * Custom metrics created while building a chart inside a dashboard are
     * kept on the dashboard and offered when building later charts there.
     * Off by default.
     */
    FeatureFlags["DashboardCustomMetrics"] = "dashboard-custom-metrics";
    /**
     * Build and edit charts in a full-screen modal over the dashboard
     * instead of navigating away. Off by default.
     */
    FeatureFlags["InDashboardChartEditor"] = "in-dashboard-chart-editor";
    /**
     * AI agent battle mode: send one prompt to two models in paired threads
     * and compare the answers side by side with response timings. Internal
     * experiment, off by default.
     */
    FeatureFlags["AiAgentBattleMode"] = "ai-agent-battle-mode";
    /**
     * Organization-scoped: keep dbt's partial_parse.msgpack from a compile on
     * the worker and seed the next compile of the same project and dbt source
     * with it, so `dbt ls` re-parses only changed files. dbt checks file
     * hashes, profile, vars and its own version and falls back to a full parse
     * on any mismatch. Off by default.
     */
    FeatureFlags["DbtPartialParse"] = "dbt-partial-parse";
    /**
     * Organization-scoped: Athena connections can authenticate with this
     * instance's Google workload identity instead of AWS keys. Only enable on
     * instances whose pods run as their own Google service account.
     */
    FeatureFlags["AthenaWebIdentityAuth"] = "athena-web-identity-auth";
    /**
     * Organization-scoped, off by default: BigQuery workload identity federation.
     * Only enable on instances whose pods run as their own Google service account.
     */
    FeatureFlags["BigqueryWorkloadIdentityAuth"] = "bigquery-workload-identity-auth";
    /**
     * Organization-scoped: org admins can bring their own AI provider keys,
     * restrict model visibility, and route those keys through a custom base
     * URL (an OpenAI/Anthropic/Gemini-compatible gateway). Off by default;
     * while off, stored org provider config is ignored and the instance
     * providers are used. Enable it for an org BEFORE it stores a key.
     */
    FeatureFlags["OrgAiCustomProviders"] = "org-ai-custom-providers";
})(FeatureFlags || (FeatureFlags = {}));
export function isFeatureFlags(value) {
    return Object.values(FeatureFlags).includes(value);
}
//# sourceMappingURL=featureFlags.js.map